import { useAppContext } from "../../context/AppContext";
import { GradientBorderBox } from "../GradientBorderBox";
import Input from "../Input";
import ConnectWalletBtn from "../ConnectWalletBtn";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "react-toastify";
import { apiServices } from "../../utils/apiServices";
import { SN_VAL_ADDRS } from "../../utils/const";
import { formatAmount, parseContractEvents, ss58ToPublicKey } from "../../utils";
import { useEVMContext } from "../../context/EVMContext";
import { ethers } from "ethers";

const ManulPanel = ({
    refreshFunc,
    subnetInfo,
    traderData
}: {
    refreshFunc: () => void;
    subnetInfo: any;
    traderData: any;
}) => {
    const { setIsLoading, isLoading } = useAppContext();
    const { signer, accounts, poolContract } = useEVMContext();
    const { id } = useParams<{ id: string }>();
    const [taoAmount, setTaoAmount] = useState<number>(0);
    const [alphaAmount, setAlphaAmount] = useState<number>(0);
    const [sellTaoAmount, setSellTaoAmount] = useState<number>(0);
    const [sellAlphaAmount, setSellAlphaAmount] = useState<number>(0);

    // Select an item from traderData's stakes list where unstaked value is false
    const selectedStake = traderData?.stakes?.find((stake: any) => stake.unstaked === false);

    const handleStake = async () => {
        if (!accounts[0]) {
            toast.error('Trader ID is not defined');
            return;
        }
        if (!poolContract || !signer) {
            toast.error('EVM Wallet is not connected');
            return;
        }
        setIsLoading(true);
        const connectedWalletAddress = await signer?.getAddress();

        try {
            poolContract.stakeToSubnet(
                Number(id),
                ethers.parseEther(taoAmount.toString()),
                // '0xbaf1b6c3dc30d7e00d2f74e5443e934f8a026038e03063718786b2b71efe765e'
                ss58ToPublicKey(SN_VAL_ADDRS[Number(id)])
            ).then((tx: any) => {
                return tx.wait();
            }).then(async (receipt: any) => {
                // Parse event from logs
                parseContractEvents(receipt, poolContract);
                toast.success('Stake successful!');
    
                await apiServices.pool.refresh(connectedWalletAddress, false);
                refreshFunc();
            }).catch((error: any) => {
                console.error('Error while deposits:', error);
                toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
            });
        } catch (error) {
            console.log(`Failed to stake:`, error);
            toast.error(`Failed to stake. Please try again.`);
        }
        setIsLoading(false);
    };

    const handleUnstake = async () => {
        if (!accounts[0]) {
            toast.error('Error: Trader ID is not defined');
            return;
        }
        if (!poolContract || !signer) {
            toast.error('EVM Wallet is not connected');
            return;
        }
        setIsLoading(true);
        const connectedWalletAddress = await signer?.getAddress();
       
        try {
            poolContract.unstakeAllFromSubnet(
                Number(id),
            ).then((tx: any) => {
                return tx.wait();
            }).then(async (receipt: any) => {
                // Parse event from logs
                parseContractEvents(receipt, poolContract);
                toast.success('Unstake successful!');
    
                await apiServices.pool.refresh(connectedWalletAddress, false);
                refreshFunc();
            }).catch((error: any) => {
                console.error('Error while deposits:', error);
                toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
            });
        } catch (error) {
            console.log(`Failed to stake:`, error);
            toast.error(`Failed to stake. Please try again.`);
        }
        setIsLoading(false);
    };

    const handleMaxBuy = () => {
        setTaoAmount(Number(traderData?.available_amount?.toString() || "0"));
        const alphaAmount = Number(traderData?.available_amount?.toString() || "0") / Number(subnetInfo?.price?.toString() || "0");
        setAlphaAmount(alphaAmount);
    };

    const handleMaxSell = () => {
        const maxSellTaoAmount = selectedStake ? Number(selectedStake?.current_stake_balance?.toString() || 0) : 0;
        setSellTaoAmount(maxSellTaoAmount);
        setSellAlphaAmount(Number(selectedStake?.alpha_amount?.toString() || 0));
    };

    const buyTaoChange = (e: any) => {
        setTaoAmount(Number(e.target.value));
        setAlphaAmount(Number(e.target.value) / Number(subnetInfo?.price?.toString() || "0"));
    };

    const buyAlphaChange = (e: any) => {
        setAlphaAmount(Number(e.target.value));
        setTaoAmount(Number(e.target.value) * Number(subnetInfo?.price?.toString() || "0"));
    };

    const sellTaoChange = (e: any) => {
        setSellTaoAmount(Number(e.target.value));
        setSellAlphaAmount(Number(e.target.value) / Number(subnetInfo?.price?.toString() || "0"));
    };
    
    const sellAlphaChange = (e: any) => {
        setSellAlphaAmount(Number(e.target.value));
        setSellTaoAmount(Number(e.target.value) * Number(subnetInfo?.price?.toString() || "0"));
    };

    return <div>
        <div className="flex gap-[12px]">
            <GradientBorderBox title="Buy" className="p-6 w-full">
                <div className="flex flex-col gap-[12px]">
                    <div className="flex flex-col gap-[8px]">
                        <Input props={{ type: "number", placeholder: "0.0000", value: taoAmount, onChange: buyTaoChange }} afterAddon={<span>τ</span>} />
                        <Input props={{ type: "number", placeholder: "0.0000", value: alphaAmount, onChange: buyAlphaChange }} afterAddon={<span>{subnetInfo?.symbol}</span>} />
                    </div>
                    <div className="flex items-center w-full justify-end gap-[8px]">
                        <button className="bg-transparent border-0 outline-none text-[12px] underline text-black dark:text-white" onClick={handleMaxBuy}>
                            Max
                        </button>
                        <span className="text-[12px] text-black/50 dark:text-white/50">Available: τ{formatAmount(traderData?.available_amount?.toString() || "0")}</span>
                    </div>
                    {
                        accounts[0] ?
                            <button
                                className="w-full p-[14px] rounded-[8px] font-medium bg-[#5AD700] text-white hover:opacity-80 transition-all duration-200 cursor-pointer"
                                disabled={isLoading || taoAmount <= 0}
                                onClick={handleStake}
                            >
                                {isLoading ? <div className="flex items-center justify-center gap-[8px]">
                                    <div className="w-[16px] h-[16px] border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Processing...</span>
                                </div> : "Buy"}
                            </button>
                            :
                            <ConnectWalletBtn className="rounded-[8px]" />
                    }
                </div>
            </GradientBorderBox>
            <GradientBorderBox title="Sell" className="p-6 w-full">
                <div className="flex flex-col gap-[12px]">
                    <div className="flex flex-col gap-[8px]">
                        <Input props={{ type: "number", placeholder: "0.0000", value: sellTaoAmount, onChange: sellTaoChange }} afterAddon={<span>τ</span>} />
                        <Input props={{ type: "number", placeholder: "0.0000", value: sellAlphaAmount, onChange: sellAlphaChange }} afterAddon={<span>{subnetInfo?.symbol}</span>} />
                    </div>
                    <div className="flex items-center w-full justify-end gap-[8px]">
                        <button className="bg-transparent border-0 outline-none text-[12px] underline text-black dark:text-white" onClick={handleMaxSell}>
                            Max
                        </button>
                        <span className="text-[12px] text-black/50 dark:text-white/50">Available: τ{selectedStake ? formatAmount(selectedStake?.current_stake_balance?.toString() || "0") : formatAmount("0")}</span>
                    </div>
                    {
                        accounts[0] ?
                            <button
                                className="w-full p-[14px] rounded-[8px] font-medium bg-[#FF2F00] text-white hover:opacity-80 transition-all duration-200 cursor-pointer"
                                disabled={isLoading || sellTaoAmount <= 0}
                                onClick={handleUnstake}
                            >
                                {isLoading ? <div className="flex items-center justify-center gap-[8px]">
                                    <div className="w-[16px] h-[16px] border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Processing...</span>
                                </div> : "Sell"}
                            </button>
                            :
                            <ConnectWalletBtn className="rounded-[8px]" />
                    }
                </div>
            </GradientBorderBox>
        </div>
    </div>;
};

export default ManulPanel;
